import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';

interface Props {
  data: any; // segment KPI object
}

/**
 * Detail panel behind "Konversionen & Dauern" – stage counts, conversion rates and
 * average durations. Rendered underneath the KPI row once the user expands it.
 */
const PipelineKpiSegment: React.FC<Props> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  if (!data) return null;

  const { stageCounts = {}, conversionRates = {}, avgDurations = {} } = data;

  const stages = Object.keys(stageCounts);

  const translateStage = (key: string): string => {
    const map: Record<string, string> = {
      answered: t('answered'),
      bookedMeeting: t('bookedMeeting'),
      qualifiedBant: t('qualifiedBant'),
      customer: t('customer'),
      lost: t('lost'),
      interviewStage: t('interviewStage'),
      interviewStageInt: t('interviewStageInt'),
      cvToClient: t('cvToClient'),
      clientInterview: t('clientInterview'),
      hired: t('hired'),
    };
    return map[key] || key;
  };

  const formatDuration = (ms: number): string => {
    if (!ms || isNaN(ms)) return '-';
    if (ms >= 86400000) return `${(ms / 86400000).toFixed(1)}d`;
    return `${(ms / 3600000).toFixed(1)}h`;
  };

  const renderTransition = (key: string) => (
    <>
      {key.split('->').map((part, idx) => (
        <React.Fragment key={idx}>
          {idx === 1 ? '→ ' : ''}
          {translateStage(part)}
          {idx === 0 ? ' ' : ''}
        </React.Fragment>
      ))}
    </>
  );

  const renderRow = (key: string, label: React.ReactNode, value: React.ReactNode) => (
    <Stack key={key} direction={'row'} justifyContent={'space-between'} gap={'12px'} className={classes.row}>
      <Typography className={classes.rowLabel}>{label}</Typography>
      <Typography className={classes.rowValue}>{value}</Typography>
    </Stack>
  );

  return (
    <Stack direction={'row'} gap={'12px'} flexWrap={'wrap'} className={classes.panel}>
      <Box className={classes.block}>
        <Typography className={classes.blockTitle}>{t('stageCounts')}</Typography>
        {stages.map((s) => renderRow(s, translateStage(s), stageCounts[s]))}
      </Box>

      <Box className={classes.block}>
        <Typography className={classes.blockTitle}>{t('conversionPercent')}</Typography>
        {Object.keys(conversionRates).map((k) => renderRow(k, renderTransition(k), `${Math.round(conversionRates[k] * 100)}%`))}
      </Box>

      <Box className={classes.block}>
        <Typography className={classes.blockTitle}>{t('avgDuration')}</Typography>
        {Object.keys(avgDurations).map((k) => renderRow(k, renderTransition(k), formatDuration(avgDurations[k] as number)))}
      </Box>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  panel: {
    width: '100%',
  },
  block: {
    flex: 1,
    minWidth: 220,
    background: '#FFF',
    borderRadius: '12px',
    padding: '16px 20px',
  },
  blockTitle: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    marginBottom: theme.spacing(1),
  },
  row: {
    padding: '3px 0',
  },
  rowLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.dark,
  },
  rowValue: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
}));

export default PipelineKpiSegment;
